import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  getDocs,
  onSnapshot,
  query,
  where,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import type {
  NetWorthAllocationBreakdown,
  NetWorthSnapshot,
  NetWorthSnapshotTotals,
} from '../types';
import { db } from '../lib/firebase';
import { COLLECTION_NET_WORTH_SNAPSHOTS } from '../lib/firestorePaths';

const HISTORY_LIMIT = 90;

export type NetWorthHistoryState = {
  snapshots: NetWorthSnapshot[];
  loading: boolean;
  error: string | null;
};

const EMPTY_STATE: NetWorthHistoryState = {
  snapshots: [],
  loading: false,
  error: null,
};

function asNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function asTotals(value: unknown): NetWorthSnapshotTotals {
  const x = (value ?? {}) as Record<string, unknown>;
  return {
    assets: asNumber(x.assets),
    liabilities: asNumber(x.liabilities),
    investments: asNumber(x.investments),
    bankCash: asNumber(x.bankCash),
    realEstate: asNumber(x.realEstate),
    netWorth: asNumber(x.netWorth),
  };
}

function asAllocation(value: unknown): NetWorthAllocationBreakdown | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const entries = Object.entries(value as Record<string, unknown>);
  const allocation: NetWorthAllocationBreakdown = {};
  for (const [key, amount] of entries) {
    if (typeof amount === 'number' && Number.isFinite(amount)) {
      allocation[key] = amount;
    }
  }
  return Object.keys(allocation).length > 0 ? allocation : undefined;
}

function docItemId(d: QueryDocumentSnapshot, data: { id?: unknown }): string {
  if (typeof data.id === 'string' && data.id.trim()) return data.id.trim();
  const parts = d.id.split('__');
  return parts.length > 1 ? parts.slice(1).join('__') : d.id;
}

function docToNetWorthSnapshot(d: QueryDocumentSnapshot): NetWorthSnapshot {
  const x = d.data() as Record<string, unknown>;
  const now = new Date().toISOString();
  const id = docItemId(d, x);
  const snapshotDate =
    typeof x.snapshotDate === 'string' && x.snapshotDate.trim()
      ? x.snapshotDate.trim()
      : id;

  return {
    id,
    userId: typeof x.userId === 'string' ? x.userId : '',
    snapshotDate,
    personal: asTotals(x.personal),
    business: asTotals(x.business),
    combined: asTotals(x.combined),
    allocation: asAllocation(x.allocation),
    createdAt: typeof x.createdAt === 'string' ? x.createdAt : now,
  };
}

function sortSnapshotsByDate(snapshots: NetWorthSnapshot[]): NetWorthSnapshot[] {
  return [...snapshots].sort((a, b) => a.snapshotDate.localeCompare(b.snapshotDate));
}

/** Keep the most recent `limit` snapshots, returned ascending by `snapshotDate`. */
export function selectRecentNetWorthHistory(
  snapshots: NetWorthSnapshot[],
  limit = HISTORY_LIMIT
): NetWorthSnapshot[] {
  if (snapshots.length <= limit) {
    return sortSnapshotsByDate(snapshots);
  }

  return sortSnapshotsByDate(snapshots)
    .slice(-limit);
}

export function useNetWorthHistory(userId: string): NetWorthHistoryState {
  const [snapshots, setSnapshots] = useState<NetWorthSnapshot[]>([]);
  const [loading, setLoading] = useState(Boolean(userId));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setSnapshots([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    const qSnapshots = query(
      collection(db, COLLECTION_NET_WORTH_SNAPSHOTS),
      where('userId', '==', userId)
    );

    const applySnapshots = (docs: QueryDocumentSnapshot[]) => {
      setSnapshots(selectRecentNetWorthHistory(docs.map(docToNetWorthSnapshot)));
    };

    (async () => {
      setLoading(true);
      setError(null);

      try {
        const snap = await getDocs(qSnapshots);
        if (cancelled) return;

        applySnapshots(snap.docs);

        unsubscribe = onSnapshot(
          qSnapshots,
          (next) => applySnapshots(next.docs),
          (e) => setError(e.message)
        );
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load net worth history');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [userId]);

  return useMemo(
    () => ({
      snapshots,
      loading,
      error,
    }),
    [snapshots, loading, error]
  );
}

export function emptyNetWorthHistoryState(): NetWorthHistoryState {
  return EMPTY_STATE;
}
